import React, { useEffect, useState } from "react";
import {
  Badge,
  Spinner,
  Modal,
  Table,
  Button,
  Row,
  Col,
} from "react-bootstrap";
import {
  FiArrowRight,
  FiInfo,
  FiX,
  FiTrendingUp,
  FiShoppingBag,
  FiArrowUp,
  FiArrowDown,
  FiMaximize2,
} from "react-icons/fi";
import logo from "../images/TP-_logo-orizz-claim-color.png";
import "bootstrap/dist/css/bootstrap.min.css";

const WidgetCrossDocking = () => {
  const [flussi, setFlussi] = useState([]);
  const [totaleGiornaliero, setTotaleGiornaliero] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Stato per la gestione dell'ordinamento delle colonne
  const [sortConfig, setSortConfig] = useState({
    key: "Doc_Fornitore",
    direction: "desc",
  });

  useEffect(() => {
    fetch("ordini_urgenti.json")
      .then((res) => res.json())
      .then((data) => {
        const tuttiGliOrdini = data?.CROSS_DOCKING || [];

        const adesso = new Date();
        const offset = adesso.getTimezoneOffset() * 60000;
        const oggiLocale = new Date(adesso - offset)
          .toISOString()
          .split("T")[0];

        // FILTRO: Solo record di oggi
        const listaRaw = tuttiGliOrdini.filter((item) => {
          const dataStr = item.data_doc
            ? String(item.data_doc).split(" ")[0]
            : null;
          return dataStr === oggiLocale;
        });

        // 1. CALCOLO TOTALE GIORNALIERO PURO (Importo Cliente)
        const totaleG = listaRaw.reduce((acc, item) => {
          const valore = parseFloat(item["Importo cli"]) || 0;
          return acc + valore;
        }, 0);

        // 2. RAGGRUPPAMENTO PER FLUSSO (Doc_Cliente + Doc_Fornitore)
        const gruppi = listaRaw.reduce((acc, item) => {
          const key = `${item.Doc_Cliente}_${item.Doc_Fornitore}`;
          const importoCliente = parseFloat(item["Importo cli"]) || 0;
          const importoFornitore = parseFloat(item["Importo for"]) || 0;

          if (!acc[key]) {
            acc[key] = {
              Doc_Cliente: item.Doc_Cliente,
              Doc_Fornitore: item.Doc_Fornitore,
              Tipo_Doc_Cliente: item.Tipo_Doc_Cliente || "",
              Tipo_Doc_Fornitore: item.Tipo_Doc_Fornitore || "",
              Cliente: item.Cliente,
              Fornitore: item.Fornitore,
              Nazione_Cliente: item["NAZIONE CLIENTE"],
              Nazione_Fornitore: item["NAZIONE FORNITORE"],
              Regione_Fornitore: item["REGIONE FORNITORE"],
              Provincia_Fornitore: item["PROVINCIA FORNITORE"],
              data_doc: item.data_doc,
              articoli: [],
              totaleCliente: 0,
              totaleFornitore: 0,
            };
          }

          acc[key].articoli.push({
            articolo: item.articolo,
            precodice: item.precodice,
            quantita: parseFloat(item.quantita) || 1,
            importoCli: importoCliente,
            importoFor: importoFornitore,
          });

          acc[key].totaleCliente += importoCliente;
          acc[key].totaleFornitore += importoFornitore;
          return acc;
        }, {});

        // Integrazione Margini e Profitto
        const arrayFlussi = Object.values(gruppi).map((flusso) => {
          const profitto = flusso.totaleCliente - flusso.totaleFornitore;
          const percentualeGuadagno =
            flusso.totaleCliente > 0
              ? (profitto / flusso.totaleCliente) * 100
              : 0;
          return {
            ...flusso,
            numArticoli: flusso.articoli.length,
            profitto,
            percentualeGuadagno,
          };
        });

        setFlussi(arrayFlussi);
        setTotaleGiornaliero(totaleG);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Errore Cross Docking:", err);
        loading && setLoading(false);
      });
  }, []);

  // LOGICA DI ORDINAMENTO DINAMICO
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedFlussi = React.useMemo(() => {
    let sortableItems = [...flussi];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        // Gestione ordinamento numerico o stringa locale sensibile
        if (typeof valA === "number" && typeof valB === "number") {
          return sortConfig.direction === "asc" ? valA - valB : valB - valA;
        } else {
          return sortConfig.direction === "asc"
            ? String(valA).localeCompare(String(valB), undefined, {
                numeric: true,
              })
            : String(valB).localeCompare(String(valA), undefined, {
                numeric: true,
              });
        }
      });
    }
    return sortableItems;
  }, [flussi, sortConfig]);

  const handleOpenDettaglio = (flusso) => {
    setSelectedOrder(flusso);
    setShowModal(true);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <FiArrowUp className="ms-1 text-danger" />
    ) : (
      <FiArrowDown className="ms-1 text-danger" />
    );
  };

  const renderTabellaFlussi = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="danger" />
        </div>
      );
    }
    if (sortedFlussi.length === 0) {
      return (
        <div className="text-center py-5 text-muted small">
          Nessun flusso di Cross Docking rilevato oggi.
        </div>
      );
    }
    return (
      <Table
        hover
        className="align-middle mb-0 text-nowrap"
        style={{ fontSize: "0.8rem" }}
      >
        <thead>
          <tr>
            <th
              className="th-sortable"
              onClick={() => requestSort("Doc_Fornitore")}
            >
              Doc. Fornitore {getSortIcon("Doc_Fornitore")}
            </th>
            <th
              className="th-sortable"
              onClick={() => requestSort("Fornitore")}
            >
              Fornitore {getSortIcon("Fornitore")}
            </th>
            <th
              className="th-sortable"
              onClick={() => requestSort("Doc_Cliente")}
            >
              Doc. Cliente {getSortIcon("Doc_Cliente")}
            </th>
            <th
              className="th-sortable"
              onClick={() => requestSort("Cliente")}
            >
              Cliente {getSortIcon("Cliente")}
            </th>
            <th
              className="th-sortable text-center"
              onClick={() => requestSort("numArticoli")}
            >
              Articoli {getSortIcon("numArticoli")}
            </th>
            <th
              className="th-sortable text-end"
              onClick={() => requestSort("totaleCliente")}
            >
              Tot. Vendita {getSortIcon("totaleCliente")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedFlussi.map((flusso, idx) => (
            <tr
              key={idx}
              className="tr-clickable"
              onClick={() => handleOpenDettaglio(flusso)}
            >
              <td className="fw-bold" style={{ fontFamily: "monospace" }}>
                {flusso.Tipo_Doc_Fornitore
                  ? `${flusso.Tipo_Doc_Fornitore} `
                  : ""}
                {flusso.Doc_Fornitore}
              </td>
              <td
                title={flusso.Fornitore}
                style={{
                  maxWidth: "160px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <span className="fw-semibold">{flusso.Fornitore}</span>{" "}
                <small className="text-muted">
                  ({flusso.Nazione_Fornitore})
                </small>
              </td>
              <td className="fw-bold" style={{ fontFamily: "monospace" }}>
                {flusso.Tipo_Doc_Cliente
                  ? `${flusso.Tipo_Doc_Cliente} `
                  : ""}
                {flusso.Doc_Cliente}
              </td>
              <td
                title={flusso.Cliente}
                style={{
                  maxWidth: "160px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <span className="fw-semibold">{flusso.Cliente}</span>{" "}
                <small className="text-muted">
                  ({flusso.Nazione_Cliente})
                </small>
              </td>
              <td className="text-center fw-bold">
                <Badge bg="light" text="dark" className="border">
                  {flusso.numArticoli}
                </Badge>
              </td>
              <td className="text-end text-secondary fw-semibold">
                €{" "}
                {flusso.totaleFornitore.toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <div
      className="card"
      style={{
        borderRadius: "10px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "380px",
        background: "#fff",
      }}
    >
      {/* Header Widget */}
      <div
        className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white"
        style={{ zIndex: 10, flexShrink: 0 }}
      >
        <div className="d-flex align-items-center">
          <span
            role="button"
            onClick={() => setShowFullscreen(true)}
            className="title-expand"
            style={{
              fontWeight: "800",
              fontSize: "0.8rem",
              color: "#1a1a1a",
              letterSpacing: "1px",
              marginRight: "8px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            CROSS DOCKING
          </span>
          <button
            type="button"
            className="cross-info-btn"
            aria-label="Informazioni sul Cross Docking"
            onClick={() => setShowInfoModal(true)}
          >
            <FiInfo size={14} />
          </button>
        </div>

        <div className="d-flex gap-2 align-items-center">
          <Badge
            pill
            bg="dark"
            className="px-2 py-1.5"
            style={{ fontSize: "0.65rem", fontWeight: "700" }}
          >
            {flussi.length} OPERAZIONI
          </Badge>
          <Badge
            pill
            bg="success"
            className="px-2 py-1.5 shadow-sm text-uppercase"
            style={{
              fontSize: "0.70rem",
              fontWeight: "700",
              letterSpacing: "0.5px",
            }}
          >
            Fatt. Clienti Oggi: €{" "}
            {totaleGiornaliero.toLocaleString("it-IT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Badge>
        </div>
      </div>

      <style>{`
        /* Unico contenitore di scroll: gestisce sia verticale che orizzontale */
        .table-responsive-cd { overflow: auto; flex: 1 1 auto; min-height: 0; }
        .table-responsive-cd::-webkit-scrollbar { width: 6px; height: 6px; }
        .table-responsive-cd::-webkit-scrollbar-thumb { background: #ced4da; border-radius: 10px; }

        .th-sortable { cursor: pointer; user-select: none; white-space: nowrap; font-size: 0.65rem !important; text-transform: uppercase; background: #f8f9fa !important; position: sticky; top: 0; z-index: 5; }
        .th-sortable:hover { background: #f1f1f1 !important; }
        
        .tr-clickable { cursor: pointer; transition: background 0.15s ease-in-out; }
        .tr-clickable:hover { background: #fdf2f2 !important; }
        
        .custom-modal-content { max-width: 750px !important; width: 100% !important; margin: 0 auto !important; border-radius: 20px !important; box-shadow: 0 15px 40px rgba(0,0,0,0.15) !important; }

        .title-expand .expand-icon { opacity: 0.5; transition: opacity 0.15s ease-in-out; }
        .title-expand:hover .expand-icon { opacity: 1; }
        .title-expand:hover { color: #b81f25 !important; }

        .fullscreen-table-container { flex: 1 1 auto; min-height: 0; overflow: auto; }
        .kpi-container { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .kpi-card { padding: 14px; border-radius: 12px; background: #fdfdfd; border: 1px solid #eaeaea; }
        .kpi-title { font-size: 0.65rem; font-weight: 700; color: #8c8c8c; text-transform: uppercase; margin-bottom: 4px; }
        .kpi-value { font-size: 0.95rem; font-weight: 800; color: #1a1a1a; }

        .cross-info-btn { width: 27px; height: 27px; border: 1px solid #dfe3e8; border-radius: 50%; background: #fff; color: #0d6efd; display: inline-flex; align-items: center; justify-content: center; padding: 0; cursor: pointer; transition: all 0.15s ease; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .cross-info-btn:hover { background: #e7f1ff; border-color: #b6d4fe; transform: translateY(-1px); }
        .cross-info-backdrop { z-index: 20990 !important; }
        .cross-info-card { border: 1px solid #e9ecef; border-radius: 14px; background: #fff; padding: 16px; height: 100%; }
        .cross-info-step { width: 28px; height: 28px; min-width: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.72rem; font-weight: 800; background: #e7f1ff; color: #0d6efd; }
        .cross-info-list { margin: 0; padding-left: 18px; color: #495057; font-size: 0.82rem; }
        .cross-info-list li + li { margin-top: 7px; }
      `}</style>

      {/* Area Tabella di Riepilogo — UNICO contenitore scrollabile */}
      <div className="table-responsive-cd">{renderTabellaFlussi()}</div>



      {/* MODALE INFORMATIVA CROSS DOCKING */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        size="lg"
        scrollable
        className="p-5"
        style={{ zIndex: 21000 }}
        backdropClassName="cross-info-backdrop"
      >
        <Modal.Header closeButton className="border-bottom bg-white px-4 py-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
              style={{ width: "40px", height: "40px" }}
            >
              <FiInfo size={20} />
            </div>
            <div>
              <Modal.Title className="fw-bold" style={{ fontSize: "1.05rem" }}>
                Come leggere il Cross Docking
              </Modal.Title>
              <div className="text-muted mt-1" style={{ fontSize: "0.78rem" }}>
                Guida ai flussi fornitore-cliente, agli importi e al dettaglio economico delle operazioni.
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="bg-light p-4">
          <div className="bg-primary-subtle border border-primary-subtle rounded-4 p-3 mb-3">
            <div className="fw-bold text-primary mb-1">A cosa serve</div>
            <div className="text-dark" style={{ fontSize: "0.84rem", lineHeight: 1.55 }}>
              Il widget monitora i flussi di <strong>Cross Docking</strong> del giorno: collega il documento
              del fornitore al relativo documento cliente e consente di controllare articoli, importi di acquisto,
              importi di vendita e marginalità senza leggere singolarmente tutte le righe del file sorgente.
            </div>
          </div>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">1</span>
                  <div>
                    <div className="fw-bold text-dark">Quali operazioni vengono mostrate</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>Solo i flussi del giorno corrente</div>
                  </div>
                </div>
                <ul className="cross-info-list">
                  <li>Il sistema legge la sezione <strong>CROSS_DOCKING</strong> del file dati.</li>
                  <li>Vengono considerate solo le righe in cui <strong>data_doc</strong> corrisponde alla data di oggi.</li>
                  <li>L'orario eventualmente presente nella data non modifica il confronto: viene utilizzato il solo giorno.</li>
                  <li>Se non esistono flussi per oggi, il widget mostra il messaggio “Nessun flusso di Cross Docking rilevato oggi”.</li>
                </ul>
              </div>
            </Col>

            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">2</span>
                  <div>
                    <div className="fw-bold text-dark">Come viene creato un flusso</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>Associazione documento cliente + documento fornitore</div>
                  </div>
                </div>
                <ul className="cross-info-list">
                  <li>Le righe vengono raggruppate usando la coppia <strong>Doc. Cliente + Doc. Fornitore</strong>.</li>
                  <li>Più articoli appartenenti alla stessa coppia documentale vengono riuniti in un'unica operazione.</li>
                  <li>Il badge <strong>OPERAZIONI</strong> indica quindi il numero di flussi raggruppati, non il numero delle singole righe articolo.</li>
                  <li>Il numero mostrato nella colonna <strong>Articoli</strong> rappresenta invece quante righe articolo compongono quel flusso.</li>
                </ul>
              </div>
            </Col>
          </Row>

          <div className="cross-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="cross-info-step">3</span>
              <div>
                <div className="fw-bold text-dark">Come leggere la tabella</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>Origine fornitore, destinazione cliente e valore economico</div>
              </div>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <ul className="cross-info-list">
                  <li><strong>Doc. Fornitore:</strong> documento di acquisto collegato al flusso.</li>
                  <li><strong>Fornitore:</strong> soggetto di provenienza della merce e relativa nazione.</li>
                  <li><strong>Doc. Cliente:</strong> documento di vendita associato alla stessa operazione.</li>
                </ul>
              </Col>
              <Col md={6}>
                <ul className="cross-info-list">
                  <li><strong>Cliente:</strong> destinazione commerciale finale e relativa nazione.</li>
                  <li><strong>Articoli:</strong> numero di righe articolo comprese nel flusso.</li>
                  <li><strong>Tot. Vendita:</strong> nel codice attuale la cella visualizza il totale fornitore, cioè la somma di <strong>Importo for</strong> del gruppo.</li>
                </ul>
              </Col>
            </Row>
            <div className="mt-3 p-2 px-3 rounded-3 bg-warning-subtle border border-warning-subtle" style={{ fontSize: "0.78rem" }}>
              <strong>Nota tecnica:</strong> l'intestazione della colonna è “Tot. Vendita”, ma il valore renderizzato è
              <strong> totaleFornitore</strong>. Se vuoi che rappresenti realmente la vendita cliente, la cella deve usare
              <strong> totaleCliente</strong>.
            </div>
          </div>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">4</span>
                  <div>
                    <div className="fw-bold text-dark">Fatturato clienti di oggi</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>Valore del badge verde</div>
                  </div>
                </div>
                <div className="text-dark" style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
                  Il badge <strong>Fatt. Clienti Oggi</strong> somma il campo <strong>Importo cli</strong> di tutte le righe
                  Cross Docking della giornata, prima del raggruppamento per documenti. Rappresenta quindi il valore cliente
                  complessivo dei flussi rilevati oggi.
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">5</span>
                  <div>
                    <div className="fw-bold text-dark">Ordinamento</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>Confronta rapidamente i flussi</div>
                  </div>
                </div>
                <ul className="cross-info-list">
                  <li>Clicca su un'intestazione per ordinare la tabella.</li>
                  <li>Un secondo clic sulla stessa colonna inverte l'ordinamento crescente/decrescente.</li>
                  <li>Puoi ordinare per documento, fornitore, cliente, numero articoli e totale.</li>
                  <li>Le frecce indicano la direzione dell'ordinamento attualmente applicato.</li>
                </ul>
              </div>
            </Col>
          </Row>

          <div className="cross-info-card mb-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="cross-info-step">6</span>
              <div>
                <div className="fw-bold text-dark">Dettaglio del flusso</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>Apri una riga per vedere articoli e valori</div>
              </div>
            </div>
            <Row className="g-3">
              <Col md={6}>
                <ul className="cross-info-list">
                  <li>Il riquadro superiore mostra la direttrice <strong>Fornitore → Cliente</strong>.</li>
                  <li>Per ogni articolo vengono mostrati codice, precodice, quantità, valore acquisto fornitore e valore vendita cliente.</li>
                  <li><strong>Totale Acquisto</strong> è la somma degli importi fornitore del flusso.</li>
                </ul>
              </Col>
              <Col md={6}>
                <ul className="cross-info-list">
                  <li><strong>Totale Vendita</strong> è la somma degli importi cliente del flusso.</li>
                  <li><strong>Margine Netto</strong> = Totale Vendita − Totale Acquisto.</li>
                  <li>La percentuale del margine è calcolata come <strong>Margine / Totale Vendita × 100</strong>.</li>
                </ul>
              </Col>
            </Row>
          </div>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">7</span>
                  <div>
                    <div className="fw-bold text-dark">Interpretazione del margine</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>Lettura economica del flusso</div>
                  </div>
                </div>
                <ul className="cross-info-list">
                  <li><strong>Margine positivo:</strong> il valore di vendita è superiore al costo fornitore.</li>
                  <li><strong>Margine negativo:</strong> il costo fornitore supera il valore di vendita.</li>
                  <li>Il colore verde/rosso del KPI permette di individuare rapidamente il segno del margine.</li>
                </ul>
              </div>
            </Col>

            <Col md={6}>
              <div className="cross-info-card">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="cross-info-step">8</span>
                  <div>
                    <div className="fw-bold text-dark">Vista estesa</div>
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>Tabella a schermo intero</div>
                  </div>
                </div>
                <div className="d-flex gap-3 align-items-start">
                  <div className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0" style={{ width: "36px", height: "36px" }}>
                    <FiMaximize2 size={16} />
                  </div>
                  <div style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
                    Cliccando sul titolo <strong>CROSS DOCKING</strong> si apre la vista fullscreen. La logica dei dati e
                    dell'ordinamento resta la stessa, ma lo spazio disponibile permette di leggere più comodamente tutte le operazioni.
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <div className="cross-info-card">
            <div className="d-flex align-items-center gap-2 mb-3">
              <span className="cross-info-step">9</span>
              <div>
                <div className="fw-bold text-dark">Esempio pratico</div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>Come viene costruita un'operazione</div>
              </div>
            </div>
            <div className="p-3 rounded-4 border bg-light" style={{ fontSize: "0.82rem", lineHeight: 1.55 }}>
              Se tre righe articolo hanno lo stesso <strong>Doc. Cliente 1001</strong> e lo stesso
              <strong> Doc. Fornitore 5001</strong>, il widget le presenta come <strong>una sola operazione</strong> con
              <strong> 3 articoli</strong>. Gli importi cliente e fornitore delle tre righe vengono sommati e da questi
              totali vengono calcolati margine e percentuale.
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-white border-top px-4 py-3 d-flex justify-content-between">
          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
            Suggerimento: usa prima l'ordinamento della tabella e poi apri la singola riga per analizzare articoli e margine.
          </div>
          <div className="d-flex gap-2">
            <Button variant="light" onClick={() => setShowInfoModal(false)}>
              Chiudi
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowInfoModal(false);
                setShowFullscreen(true);
              }}
            >
              Apri vista estesa
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* MODALE DETTAGLIO ED ANALISI FINANZIARIA DELL'ORDINE */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        contentClassName="custom-modal-content"
      >
        <Modal.Header closeButton className="px-4 pt-4 border-0 bg-white">
          <Modal.Title
            className="d-flex align-items-center gap-2 fw-bold"
            style={{ fontSize: "1.05rem", color: "#b81f25" }}
          >
            <FiTrendingUp size={20} /> ANALISI COMPONENTI FLUSSO SELEZIONATO
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-white px-4 pb-4 pt-0">
          {/* Box Direttrici Logistiche Loggate */}
          <div
            className="p-3 mb-4 d-flex justify-content-between align-items-center rounded"
            style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <small
                className="text-muted d-block fw-bold"
                style={{ fontSize: "0.55rem" }}
              >
                PROVENIENZA ACQUISTO
              </small>
              <span
                className="fw-bold text-dark d-block text-truncate"
                style={{ fontSize: "0.8rem" }}
              >
                {selectedOrder?.Fornitore}
              </span>
              <div
                className="text-muted small"
                style={{ fontFamily: "monospace", fontSize: "0.7rem" }}
              >
                {selectedOrder?.Tipo_Doc_Fornitore}{" "}
                {selectedOrder?.Doc_Fornitore}
              </div>
            </div>
            <div className="px-3 text-danger">
              <FiArrowRight size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }} className="text-end">
              <small
                className="text-muted d-block fw-bold"
                style={{ fontSize: "0.55rem" }}
              >
                DESTINAZIONE VENDITA
              </small>
              <span
                className="fw-bold text-dark d-block text-truncate"
                style={{ fontSize: "0.8rem" }}
              >
                {selectedOrder?.Cliente}
              </span>
              <div
                className="text-muted small"
                style={{ fontFamily: "monospace", fontSize: "0.7rem" }}
              >
                {selectedOrder?.Tipo_Doc_Cliente} {selectedOrder?.Doc_Cliente}
              </div>
            </div>
          </div>

          {/* Tabella degli Articoli interni al documento */}
          <span
            className="d-block fw-bold text-muted mb-2"
            style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}
          >
            DETTAGLIO ARTICOLI IN TRANSIZIONE
          </span>
          <Table
            responsive
            hover
            className="mb-4 align-middle"
            style={{ fontSize: "0.75rem" }}
          >
            <thead>
              <tr className="table-light">
                <th>Articolo / Codice</th>
                <th className="text-center">Q.tà</th>
                <th className="text-end">Acquisto (For)</th>
                <th className="text-end">Vendita (Cli)</th>
              </tr>
            </thead>
            <tbody>
              {selectedOrder?.articoli?.map((art, i) => (
                <tr key={i}>
                  <td>
                    <div
                      className="fw-bold text-dark text-truncate"
                      style={{ maxWidth: "260px" }}
                    >
                      {art.articolo}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.65rem" }}>
                      Precodice:{" "}
                      <span className="text-dark fw-medium">
                        {art.precodice || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="text-center fw-bold">
                    {art.quantita.toFixed(0)}
                  </td>
                  <td className="text-end text-secondary fw-semibold">
                    €{" "}
                    {art.importoFor.toLocaleString("it-IT", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="text-end text-primary fw-bold">
                    €{" "}
                    {art.importoCli.toLocaleString("it-IT", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* KPI Finanziari di Riepilogo Riga */}
          <div className="kpi-container">
            <div
              className="kpi-card"
              style={{ borderLeft: "4px solid #6c757d" }}
            >
              <div className="kpi-title">
                <FiShoppingBag className="me-1" /> Totale Acquisto
              </div>
              <div className="kpi-value text-secondary">
                €{" "}
                {(selectedOrder?.totaleFornitore || 0).toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>

            <div
              className="kpi-card"
              style={{ borderLeft: "4px solid #0d6efd" }}
            >
              <div className="kpi-title">
                <FiShoppingBag className="me-1" /> Totale Vendita
              </div>
              <div className="kpi-value text-primary">
                €{" "}
                {(selectedOrder?.totaleCliente || 0).toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>

            <div
              className="kpi-card"
              style={{
                borderLeft:
                  selectedOrder?.profitto >= 0
                    ? "4px solid #198754"
                    : "4px solid #dc3545",
              }}
            >
              <div className="kpi-title d-flex justify-content-between align-items-center">
                <span>Margine Netto</span>
                <Badge
                  bg={selectedOrder?.profitto >= 0 ? "success" : "danger"}
                  style={{ fontSize: "0.55rem" }}
                >
                  {selectedOrder?.percentualeGuadagno
                    ? selectedOrder.percentualeGuadagno.toFixed(1)
                    : "0.0"}
                  %
                </Badge>
              </div>
              <div
                className={`kpi-value ${selectedOrder?.profitto >= 0 ? "text-success" : "text-danger"}`}
              >
                €{" "}
                {(selectedOrder?.profitto || 0).toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer
          className="bg-light border-top-0 justify-content-center py-2"
          style={{
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{ width: "80px", filter: "grayscale(1)", opacity: 0.3 }}
          />
        </Modal.Footer>
      </Modal>

      {/* MODALE FULLSCREEN — Vista estesa della tabella flussi */}
      <Modal
        show={showFullscreen}
        onHide={() => setShowFullscreen(false)}
        fullscreen
      >
        <Modal.Header closeButton className="bg-white border-bottom">
          <Modal.Title
            className="d-flex align-items-center gap-2 fw-bold"
            style={{ fontSize: "1rem", letterSpacing: "1px" }}
          >
            CROSS DOCKING
          </Modal.Title>
          <button
            type="button"
            className="cross-info-btn ms-2"
            aria-label="Informazioni sul Cross Docking"
            onClick={() => setShowInfoModal(true)}
          >
            <FiInfo size={15} />
          </button>
          <div className="d-flex gap-2 align-items-center ms-auto me-3">
            <Badge
              pill
              bg="dark"
              className="px-2 py-1.5"
              style={{ fontSize: "0.7rem", fontWeight: "700" }}
            >
              {flussi.length} OPERAZIONI
            </Badge>
            <Badge
              pill
              bg="success"
              className="px-2 py-1.5 shadow-sm text-uppercase"
              style={{
                fontSize: "0.75rem",
                fontWeight: "700",
                letterSpacing: "0.5px",
              }}
            >
              Fatt. Clienti Oggi: €{" "}
              {totaleGiornaliero.toLocaleString("it-IT", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Badge>
          </div>
        </Modal.Header>
        <Modal.Body
          className="bg-white p-0 d-flex flex-column h-100"
          style={{ minHeight: 0 }}
        >
          <div className="fullscreen-table-container px-3 py-2">
            {renderTabellaFlussi()}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WidgetCrossDocking;